import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { AdminUser, AdminRole } from '../../services/api'

interface AdminUserManagementProps {
  onClose: () => void
}

const ROLE_CONFIG: Record<AdminRole, { label: string; color: string; description: string }> = {
  super_admin: {
    label: 'Super Admin',
    color: 'bg-purple-500',
    description: 'Acceso total al portal'
  },
  admin: {
    label: 'Admin',
    color: 'bg-blue-500',
    description: 'Todo excepto facturacion'
  },
  viewer: {
    label: 'Viewer',
    color: 'bg-gray-500',
    description: 'Solo lectura, sin facturacion'
  }
}

export default function AdminUserManagement({ onClose }: AdminUserManagementProps) {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserRole, setNewUserRole] = useState<AdminRole>('viewer')
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchAdminUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.listAdminUsers()

      if (!result.success) {
        throw new Error(result.error || 'Error al cargar usuarios admin')
      }

      setAdminUsers(result.data?.adminUsers || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAdminUsers()
  }, [])

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newUserEmail.trim()) return

    try {
      setSaving(true)
      setError(null)

      const result = await api.createAdminUser({
        email: newUserEmail.trim(),
        adminRole: newUserRole,
        sendWelcomeEmail
      })

      if (!result.success) {
        throw new Error(result.error || 'Error al crear usuario admin')
      }

      setNewUserEmail('')
      setNewUserRole('viewer')
      setSendWelcomeEmail(true)
      setShowAddForm(false)
      await fetchAdminUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear usuario')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateRole = async (adminUserId: string, newRole: AdminRole) => {
    try {
      setError(null)
      const result = await api.updateAdminUser(adminUserId, { adminRole: newRole })

      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar rol')
      }

      await fetchAdminUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar')
    }
  }

  const handleDeleteUser = async (adminUserId: string, email: string) => {
    if (!confirm(`¿Seguro que quieres eliminar el acceso admin de ${email}?`)) {
      return
    }

    try {
      setError(null)
      const result = await api.deleteAdminUser(adminUserId)

      if (!result.success) {
        throw new Error(result.error || 'Error al eliminar usuario')
      }

      await fetchAdminUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-white">Usuarios del Portal Admin</h2>
            <p className="text-gray-400 text-sm mt-1">
              Gestiona quienes tienen acceso al portal de administracion de Jarix
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Role Legend */}
        <div className="px-6 pt-4">
          <div className="flex flex-wrap gap-3">
            {(Object.entries(ROLE_CONFIG) as [AdminRole, typeof ROLE_CONFIG[AdminRole]][]).map(([role, config]) => (
              <div key={role} className="flex items-center gap-2 text-sm">
                <span className={`w-3 h-3 rounded-full ${config.color}`}></span>
                <span className="text-gray-300">{config.label}</span>
                <span className="text-gray-500">- {config.description}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
              <button onClick={() => setError(null)} className="float-right">&times;</button>
            </div>
          )}

          {/* Add User Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mb-6 bg-jarix-orange hover:bg-jarix-pink text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Agregar Usuario Admin
            </button>
          )}

          {/* Add User Form */}
          {showAddForm && (
            <form onSubmit={handleAddUser} className="mb-6 bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-4">Agregar nuevo administrador</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email del usuario
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                    placeholder="usuario@ejemplo.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Rol
                  </label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as AdminRole)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                  >
                    <option value="viewer">Viewer - Solo lectura</option>
                    <option value="admin">Admin - Todo menos facturacion</option>
                    <option value="super_admin">Super Admin - Acceso total</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendWelcomeEmail}
                    onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-jarix-orange focus:ring-jarix-orange"
                  />
                  Enviar email de bienvenida con instrucciones de acceso
                </label>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    setNewUserEmail('')
                    setNewUserRole('viewer')
                    setSendWelcomeEmail(true)
                  }}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-jarix-orange hover:bg-jarix-pink disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  {saving ? 'Agregando...' : 'Agregar Usuario'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Si el usuario no existe, se creara una invitacion. Podra acceder cuando se registre con este email usando Google SSO o creando una cuenta.
              </p>
            </form>
          )}

          {/* Users List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jarix-orange mx-auto mb-2"></div>
              <p className="text-gray-400">Cargando usuarios admin...</p>
            </div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-8 bg-gray-700/30 rounded-lg">
              <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-gray-400">No hay usuarios administradores</p>
              <p className="text-gray-500 text-sm mt-1">Agrega usuarios para darles acceso al portal admin</p>
            </div>
          ) : (
            <div className="space-y-3">
              {adminUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-gray-700/50 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ROLE_CONFIG[user.admin_role].color}`}>
                      <span className="text-white font-semibold">
                        {user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name || user.email}</p>
                      {user.name && (
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${ROLE_CONFIG[user.admin_role].color} text-white`}>
                          {ROLE_CONFIG[user.admin_role].label}
                        </span>
                        {user.clerk_id.startsWith('pending_') && (
                          <span className="text-xs text-yellow-500">Pendiente de registro</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={user.admin_role}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value as AdminRole)}
                      className="bg-gray-600 border border-gray-500 rounded-lg px-3 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-jarix-orange"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <button
                      onClick={() => handleDeleteUser(user.id, user.email)}
                      className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                      title="Eliminar acceso admin"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700 bg-gray-800/50">
          <p className="text-gray-500 text-sm">
            {adminUsers.length} administrador{adminUsers.length !== 1 ? 'es' : ''} del portal
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
